// src/components/PresentedToCheckPanel.js
//
// Record 610 (ruling 262). On the claim page, beside the form: who the claim is presented to, as the AGREEMENT, the
// GC's DIRECTORY and the LC's DIRECTORY each say, and what the claim says. src\data\presentedToCheck.js compares them;
// this only shows the result and records the filer's choice. Information, never a block (ruling 262).
//
// WHO MAY RECORD THE CHOICE IS THE RULES', NOT THIS PANEL'S. The write is presentedTo + presentedToCheck, one update
// through claimPatch(). On a draft the author may (claimantEdit); on a filed claim the Local or General Chairperson
// who works it may (lcEdit / gcEdit). A claimant cannot change who her FILED claim was presented to (no claimant arm
// admits presentedTo after filing - unchanged by Record 610); the panel then shows the check and does not offer it.
// A refusal is shown as it comes, and nothing changes.
import React, { useState } from 'react';
import { db, auth } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { claimPatch } from '../data/claimWrite';
import { choiceRecord, entryLine } from '../data/presentedToCheck';
import { methodLabel } from '../data/claimPresentment';

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', ok: '#5FB878', text: '#DCE7F2', bad: '#E06C6C', panel: '#0A1628' };
const s = (v) => (v == null ? '' : String(v).trim());
const TONE = { ok: C.ok, info: C.dim, warn: C.accent };

function Source({ label, entry, empty }) {
  return (
    <div style={{ padding: '4px 0', borderTop: '1px solid ' + C.line }}>
      <div style={{ fontSize: 10, color: C.dim, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      {entry ? (
        <div style={{ fontSize: 12, color: C.text }}>
          {entryLine(entry)}
          {entry.addressLines && entry.addressLines.length ? <span style={{ color: C.dim }}> - {entry.addressLines.join(', ')}</span> : null}
          {s(entry.method) ? <span style={{ color: C.dim }}> - {methodLabel(entry.method)}</span> : null}
          {s(entry.note) ? <div style={{ fontSize: 11, color: C.dim }}>{entry.note}</div> : null}
        </div>
      ) : <div style={{ fontSize: 12, color: C.dim }}>{empty}</div>}
    </div>
  );
}

// result: crossCheck() output; inputs: what it was computed from ({ answer, agreement, designation, gc, lc }).
export default function PresentedToCheckPanel({ claim, claimId, user, result, inputs, onPatched }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  if (!claim || !claimId || !result || result.status === 'off') return null;
  const i = inputs || {};
  const uid = (user && user.uid) || (auth.currentUser && auth.currentUser.uid) || '';
  const officer = !!(user && (user.isAdmin || user.role === 'local_chair' || user.role === 'general_chair'));
  const mayRecord = claim.status === 'draft' ? (!!uid && s(claim.createdBy) === uid) : officer;
  const answer = s(claim.presentedTo);
  const rec = claim.presentedToCheck && s(claim.presentedToCheck.chosen) ? claim.presentedToCheck : null;

  const choose = async (line) => {
    const r = choiceRecord(result, line, i, user || auth.currentUser);
    if (!r.ok) { setMsg(r.errors.join(' ')); return; }
    setBusy(true); setMsg('');
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({ ...r.patch, updatedAt: serverTimestamp() }, user || auth.currentUser));
      setMsg('Recorded on the claim, with what each source said today.');
      if (onPatched) onPatched(r.patch);
    } catch (e) {
      setMsg('Not saved: ' + ((e && (e.code || e.message)) || 'the write was refused') + '. Nothing was changed.');
    }
    setBusy(false);
  };

  // The lines the filer may choose: each directory's officer (one button when they agree) and, when the claim already
  // names someone else, keeping the claim's own answer. Already the recorded choice -> no button for it.
  const options = [];
  (result.candidates || []).forEach((c) => { if (c.line && !options.some((o) => o.line === c.line)) options.push({ line: c.line, label: 'Use ' + c.line + (result.status === 'agree' ? ' (both directories)' : c.who === 'gc' ? ' (GC directory)' : ' (LC directory)') }); });
  if (answer && result.status === 'differ' && !options.some((o) => o.line === answer)) options.push({ line: answer, label: 'Keep what the claim says: ' + answer });
  const shown = options.filter((o) => !(rec && s(rec.chosen) === o.line && answer === o.line));
  const border = result.resolved ? (result.notices.some((n) => n.level === 'warn') ? C.accent : C.line) : C.accent;

  return (
    <div style={{ border: '1px solid ' + border, background: C.panel, padding: '10px 12px', marginBottom: 10, fontSize: 12, color: C.text }}>
      <div style={{ fontSize: 10, color: result.resolved ? C.dim : C.accent, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>
        Who the claim is presented to - checked against the agreement and both directories
      </div>
      <Source label="The agreement profile names" entry={null} empty={s(i.designation) || 'No designation is on file for this agreement.'} />
      <Source label="The General Chairperson's directory" entry={i.gc || null} empty="Names no one for this agreement yet." />
      <Source label="The Local Chairperson's directory" entry={i.lc || null} empty="Names no one for this agreement yet." />
      <div style={{ padding: '4px 0', borderTop: '1px solid ' + C.line }}>
        <div style={{ fontSize: 10, color: C.dim, textTransform: 'uppercase', letterSpacing: 1 }}>The claim says</div>
        <div style={{ fontSize: 12, color: answer ? C.text : C.accent }}>{answer || 'Not yet answered.'}</div>
        {rec ? <div style={{ fontSize: 11, color: C.dim }}>Recorded {s(rec.recordedAt).slice(0, 10)} by {s(rec.recordedByName) || 'the filer'}, with what each source said then.</div> : null}
      </div>
      {result.notices.map((n) => (
        <div key={n.key} style={{ fontSize: 11, color: TONE[n.level] || C.dim, marginTop: 6 }}>{n.level === 'warn' ? '⚠ ' : ''}{n.text}</div>
      ))}
      {mayRecord && shown.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {shown.map((o) => (
            <button key={o.line} className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} disabled={busy} onClick={() => choose(o.line)}>{busy ? 'Saving…' : o.label}</button>
          ))}
        </div>
      )}
      {!mayRecord && result.status !== 'no_directory' ? (
        <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>{claim.status === 'draft' ? 'The author of the draft chooses.' : 'The Local or General Chairperson working the claim records the choice.'}</div>
      ) : null}
      {msg ? <div style={{ fontSize: 11, color: /^Not saved/.test(msg) ? C.bad : C.ok, marginTop: 6 }}>{msg}</div> : null}
      <div style={{ fontSize: 10, color: C.dim, marginTop: 6 }}>Nothing here holds the claim up. The directories are not changed from a claim.</div>
    </div>
  );
}
